import { useState, useEffect } from "react";
import { deleteIndex } from "../../helpers/JSONTransformations";
import useInputState from "../../hooks/useInputState";
import useMaskedInputState from "../../hooks/useMaskedInputState";

const useProductSidebarInfo = (product) => {
  const initialState = {
    productImage: "",
    isImageLoading: false,
    name: "",
    description: "",
    hasVariants: false,
    acceptsDonations: false,
    price: "",
    stock: "",
    sku: "",
    selectedCategories: [],
    formHasAnInvalidField: false
  };

  const [productImage, onProductImageSelect] = useState(
    initialState.productImage
  );
  const [isImageLoading, setIsImageLoading] = useState(
    initialState.isImageLoading
  );

  const [name, onNameChange] = useMaskedInputState(initialState.name, {
    maxLength: 200
  });
  const [description, onDescriptionChange] = useMaskedInputState(
    initialState.description, {
      maxLength: 5000
    }
  );

  const [hasVariants, setHasVariants] = useState(initialState.hasVariants);
  const onToggleHasVariants = () => setHasVariants(!hasVariants);

  const [acceptsDonations, setAcceptsDonations] = useState(
    initialState.acceptsDonations
  );
  const onToggleAcceptsDonations = () => {
    const newAcceptsDonations = !acceptsDonations;

    if (newAcceptsDonations) {
      setHasVariants(false);
      onPriceChange(undefined);
    }

    setAcceptsDonations(newAcceptsDonations);
  };

  const [price, onPriceChange] = useInputState(initialState.price);

  const [stock, _onStockChange] = useInputState(initialState.stock);
  const onStockChange = (e) => {
    const val = e && e.target ? e.target.value : e;
    const numberOnly = /^\d+$/;
    _onStockChange(numberOnly.test(val) ? val : "");
  };

  const [sku, onSkuChange] = useInputState(initialState.sku);

  const [selectedCategories, setSelectedCategories] = useState(
    initialState.selectedCategories
  );

  const onSelectCategory = (category) => {
    const alreadySelectedCategoryIndex = selectedCategories.findIndex(
      (selectedCategory) => selectedCategory.id === category.id
    );

    setSelectedCategories(
      alreadySelectedCategoryIndex !== -1
        ? deleteIndex(selectedCategories, alreadySelectedCategoryIndex)
        : selectedCategories.concat(category)
    );
  };

  const onUnselectAllCategories = () => setSelectedCategories([]);

  const onUnselectCategory = (category, index) =>
    setSelectedCategories(deleteIndex(selectedCategories, index));

  const categoriesItemPredicate = (
    query = "",
    category = { name: "" },
    _index,
    exactMatch
  ) => {
    const normalizedTitle = category.name.toLowerCase();
    const normalizedQuery = query.toLowerCase();

    return exactMatch
      ? normalizedTitle === normalizedQuery
      : normalizedTitle.includes(normalizedQuery);
  };

  const categoryTagRenderer = (selectedCategory) => selectedCategory.name;

  useEffect(() => {
    resetToProduct(product);
  }, [product]);

  const [formHasAnInvalidField, setFormHasAnInvalidFields] = useState(
    initialState.formHasAnInvalidField
  );

  useEffect(() => {
    setFormHasAnInvalidFields(
      !productImage ||
        !name ||
        !description ||
        (!hasVariants && !acceptsDonations && (!price || !sku))
    );
  }, [
    productImage,
    name,
    description,
    hasVariants,
    acceptsDonations,
    price,
    sku
  ]);

  const resetState = () => {
    onProductImageSelect(initialState.productImage);
    setIsImageLoading(initialState.isImageLoading);
    onNameChange(initialState.name);
    onDescriptionChange(initialState.description);
    setHasVariants(initialState.hasVariants);
    setAcceptsDonations(initialState.acceptsDonations);
    onPriceChange(initialState.price);
    onStockChange(initialState.stock);
    onSkuChange(initialState.sku);
    setSelectedCategories(initialState.selectedCategories);
    setFormHasAnInvalidFields(initialState.formHasAnInvalidField);
  };

  const resetToProduct = (newProduct) => {
    if (newProduct && Object.keys(newProduct).length) {
      setAcceptsDonations(product.isDonation);
      setHasVariants(newProduct.variants.length);

      onProductImageSelect(newProduct.images[0].url);

      onNameChange(newProduct.name);
      onDescriptionChange(newProduct.description);

      setSelectedCategories(
        newProduct.categories.map(({ id, name }) => ({
          id,
          name
        }))
      );

      onPriceChange(newProduct.price);
      onStockChange(newProduct.stock);

      onSkuChange(newProduct.sku);
    }
  };

  return {
    productImage,
    onProductImageSelect,
    isImageLoading,
    setIsImageLoading,

    name,
    onNameChange,
    description,
    onDescriptionChange,
    selectedCategories,
    onSelectCategory,
    onUnselectCategory,
    onUnselectAllCategories,
    categoriesItemPredicate,
    categoryTagRenderer,
    hasVariants,
    setHasVariants,
    acceptsDonations,
    onToggleHasVariants,
    onToggleAcceptsDonations,
    price,
    onPriceChange,
    stock,
    onStockChange,
    sku,
    onSkuChange,
    formHasAnInvalidField,
    resetToProduct,
    resetState
  };
};

export default useProductSidebarInfo;
